import type { PureFunction } from '@bonsai-components/utility-types';
import type { OrganizationInput } from '../@types/repository-groups';
import type { RepositoryFragment } from '../__generated__/apollo-hooks';

const REPO_ID_DELIMITER = '~~';

// Placeholder for a repository with no branch, so the composite id keeps a
// stable four-part shape. This is the single source of truth for the
// substitution — the Repository.id `@client` policy delegates here (previously
// it applied `branch || 'no-branch'` itself, while this helper dropped an empty
// branch, so the two produced different ids for branchless repos).
export const EMPTY_BRANCH_PLACEHOLDER = 'no-branch';

export type RepositoryForUserOrganization = RepositoryFragment & {
  status?: string;
};

export const mapRepositoryFragmentToId: PureFunction<
  Partial<RepositoryFragment>,
  string
> = (repository) => {
  return [
    repository.__typename,
    repository.origin,
    repository.path,
    repository.branch || EMPTY_BRANCH_PLACEHOLDER
  ]
    .filter(Boolean)
    .join(REPO_ID_DELIMITER);
};

export const repositoryIdToRepositoryFragment: PureFunction<
  string,
  RepositoryFragment | null
> = (repositoryId) => {
  if (!repositoryId.includes(REPO_ID_DELIMITER)) {
    return null;
  }
  const [__typename, origin, path, branch] =
    repositoryId.split(REPO_ID_DELIMITER);

  const { organization, name } = getOrganizationAndNameFromPath(path);
  const repositoryIdFragment = {
    __typename: __typename as never,
    id: repositoryId,
    branch,
    origin,
    path,
    name,
    organization
  };

  return repositoryIdFragment;
};

const minimizeRepositoryObject = (
  fullRepositoryObject: Pick<RepositoryFragment, 'origin' | 'path' | 'branch'>
): { origin: string; path: string; branch: string } => {
  const { origin, path, branch } = fullRepositoryObject;
  return { origin, path, branch };
};

type PrunedKeys = keyof (OrganizationInput & {
  __typename: string;
  user: unknown;
});
const PRUNED_KEYS: PrunedKeys[] = ['__typename', 'id', 'user', 'parents'];

/**
 *
 * Recursively replace Repository objects with a RepositoryIdentityFragment
 */
export const minimalRepositoryReducer = <T>(param: T): T => {
  if (param === null || param === undefined) {
    return param;
  }

  if (Array.isArray(param)) {
    return param.map(minimalRepositoryReducer) as T;
  }

  if (typeof param === 'object') {
    // Check if this object has the repository fields (origin, path, branch)
    if (
      'origin' in param &&
      'path' in param &&
      'branch' in param &&
      typeof param.origin === 'string' &&
      typeof param.path === 'string' &&
      typeof param.branch === 'string'
    ) {
      return minimizeRepositoryObject(
        param as Pick<RepositoryFragment, 'origin' | 'path' | 'branch'>
      ) as T;
    }

    const keys = Object.keys(param);
    return keys.reduce((result, key) => {
      if (PRUNED_KEYS.some((prunedKey) => prunedKey === key)) {
        return result;
      }
      const value = param[key];
      result[key] = minimalRepositoryReducer(value);
      return result;
    }, {} as T);
  }

  return param as T;
};

export const getOrganizationAndNameFromPath: PureFunction<
  string,
  { organization: string; name: string }
> = (path) => {
  if (path.indexOf('/') === -1) {
    return {
      organization: path,
      name: null
    };
  } else {
    const pathParts = path.split('/');
    return {
      organization: pathParts.slice(0, -1).join('/'),
      name: pathParts.at(-1)
    };
  }
};

/**
 * Extracts display-friendly organization and name from a RepositoryFragment.
 * Derives organization and name from the path field.
 */
export const getRepositoryDisplayInfo: PureFunction<
  Pick<RepositoryFragment, 'path'> | null | undefined,
  { organization: string; name: string }
> = (repository) => {
  if (!repository?.path) {
    return { organization: '', name: '' };
  }
  return getOrganizationAndNameFromPath(repository.path);
};

export const getUniqueRepositoryIdentities: PureFunction<
  RepositoryFragment[],
  RepositoryFragment[]
> = (repositoryIdentities) => {
  return repositoryIdentities.reduce((acc, repo) => {
    const existing = acc.find(
      (r) => mapRepositoryFragmentToId(r) === mapRepositoryFragmentToId(repo)
    );
    if (!existing) {
      acc.push(repo);
    }
    return acc;
  }, [] as RepositoryFragment[]);
};

export const getUniqueOriginsFromRepositoryIdentities: PureFunction<
  RepositoryFragment[],
  string[]
> = (repositoryIdentities) => {
  return repositoryIdentities.reduce((acc, repo) => {
    if (!repo) {
      return acc;
    }

    const existing = acc.find((r) => r === repo?.origin);
    if (!existing) {
      acc.push(repo.origin);
    }
    return acc;
  }, [] as string[]);
};

// Strip scheme and trailing slash so a resourceId and an origin compare in the
// same form.
const normalizeScmHost = (value: string): string =>
  value.replace(/^https?:\/\//, '').replace(/\/+$/, '');

/**
 * Resolves the SCM connection whose resourceId matches a repository origin.
 *
 * An scmConnection `resourceId` is the configured SCM URL without scheme, so it
 * keeps a context path or port (moderne-saas#1339); before that it was the bare
 * host (e.g. "stashdev.ms.com" vs origin "stashdev.ms.com/atlassian-stash").
 * Don't assume a repository `origin` equals it: we also match at a path (`/`)
 * or port (`:`) boundary. Scheme/trailing-slash normalization is defensive.
 * Most-specific (longest) match wins for overlapping hosts.
 *
 * Generic so it works for both the full SCM connection type and the narrower
 * `{ resourceId }` shapes used in some data-grid column files.
 */
export const findScmConnectionForOrigin = <
  T extends { resourceId?: string | null }
>(
  connections: readonly T[],
  origin: string
): T | undefined => {
  const normalizedOrigin = normalizeScmHost(origin ?? '');
  if (!normalizedOrigin) {
    return undefined;
  }
  // Normalize each resourceId once, then keep only the connections whose
  // resourceId the origin matches at a path/port boundary (the boundary check avoids
  // "github.com" matching "github.community.com").
  const matches = connections
    .map((connection) => ({
      connection,
      resourceId: normalizeScmHost(connection.resourceId ?? '')
    }))
    .filter(
      ({ resourceId }) =>
        resourceId.length > 0 &&
        (normalizedOrigin === resourceId ||
          normalizedOrigin.startsWith(`${resourceId}/`) ||
          normalizedOrigin.startsWith(`${resourceId}:`))
    );
  if (matches.length <= 1) {
    return matches.at(0)?.connection;
  }
  // Most specific (longest) resourceId wins for overlapping hosts.
  return matches.reduce((longest, current) =>
    current.resourceId.length > longest.resourceId.length ? current : longest
  ).connection;
};
